import styled from '@emotion/styled';
import { Button, colors, FormField } from '@innovationdepartment/proxima-ui';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-final-form';
import ReactMarkdown from 'react-markdown';
import type { ChatMessage } from './Agent.Container';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #ccc;
  border-radius: 8px;
  max-width: 900px;
  padding: 16px;
  margin: auto;
  background-color: #f9f9f9;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 16px;
  max-height: 300px;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px;
  background-color: #fff;
  min-height: 55vh;
  height: auto;
`;

const Message = styled.div<{ isUser?: boolean }>`
  margin-bottom: 8px;
  max-width: 70%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
  background-color: ${({ isUser }) => (isUser ? colors.bigBangBlack : '#f1f0f0')};
  color: ${({ isUser }) => (isUser ? '#fff' : '#000')};
  align-self: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-left: ${({ isUser }) => (isUser ? '30%' : '0')};

  &:first-of-type {
    margin-top: 8px;
  }

  .markdown-body p:first-of-type {
    margin-top: 0;
  }

  .markdown-body p:last-of-type {
    margin-bottom: 0;
  }

  img {
    margin-top: 12px;
    max-width: 100%;
  }
`;

const InputContainer = styled.div`
  display: flex;
  gap: 8px;
`;

type AgentViewProps = {
  loading: boolean;
  messages: ChatMessage[];
};

const AgentView = ({ loading, messages }: AgentViewProps) => {
  const form = useForm();
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    form.reset();
    form.change('question', '');
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, form]);

  return (
    <ChatContainer>
      <MessagesContainer>
        {messages.map(message => (
          <Message key={message.id} isUser={message.isUser}>
            <div className="markdown-body">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
            {!!message.visBase64 && (
              <img src={`data:image/png;base64,${message.visBase64}`} alt="Visualization" />
            )}
          </Message>
        ))}
        <div ref={chatEndRef} />
      </MessagesContainer>
      <InputContainer key={`input-container-${messages.length}`}>
        <FormField
          type="input"
          name="question"
          placeholder="Ask proxima agent..."
          fullWidth
          disabled={loading}
        />
        <Button disabled={loading} label="Send" submit />
      </InputContainer>
    </ChatContainer>
  );
};

export default AgentView;
